/**
 * Console runtime configuration (build once, promote many - ASDM section 10).
 *
 * Copied from the product SPA's runtimeConfig pattern
 * (code/frontend/src/runtimeConfig.ts, spec 20260701) rather than imported: the
 * console is a separate origin and a separate build, and design.md section 11 says
 * copy, do not import.
 *
 * A single artifact resolves its environment config at runtime from a same-origin
 * static file the web container's entrypoint writes from that environment's app
 * settings, so no VITE_CONSOLE_* value is baked at build time. `loadConsoleConfig()`
 * MUST be awaited before any module that reads config initialises (see index.tsx).
 *
 * In a deployed build the endpoint is authoritative and there is NO build-time
 * fallback (fail closed). In local dev it falls back to `.env` VITE_CONSOLE_* so
 * `npm run dev` works without the Functions host.
 */
import { isViteDev, viteFallbackConfig } from "./viteDevEnv";

export interface ConsoleConfig {
  /** Origin that serves /api/cortex/console/* - the Functions app, cross-origin in DEV. */
  functionAppUrl: string;
  /** Corporate tenant that issues console tokens. Never the product tenant. */
  entraTenantId: string;
  /** Console app registration client id - the audience the console API accepts. */
  entraClientId: string;
  entraAuthority: string;
}

const EMPTY: ConsoleConfig = {
  functionAppUrl: "",
  entraTenantId: "",
  entraClientId: "",
  entraAuthority: "",
};

/**
 * Test bootstrap seam, the same one the product SPA uses: a unit test has no
 * `index.tsx` to await `loadConsoleConfig()` first, so module-load-time readers would
 * throw. Seeded by a global the test sets before importing; in a browser the global
 * never exists and every deployed path is unchanged.
 */
const _testSeed = (globalThis as { __CONSOLE_CONFIG_TEST_SEED__?: Partial<ConsoleConfig> })
  .__CONSOLE_CONFIG_TEST_SEED__;

let _config: ConsoleConfig | null = _testSeed ? { ...EMPTY, ..._testSeed } : null;

export async function loadConsoleConfig(): Promise<ConsoleConfig> {
  if (_config) {
    return _config;
  }
  try {
    const resp = await fetch("/console-config.json", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
      cache: "no-store",
    });
    if (!resp.ok) {
      throw new Error(`console-config responded ${resp.status}`);
    }
    const data = (await resp.json()) as Partial<ConsoleConfig>;
    _config = { ...EMPTY, ...data };
    return _config;
  } catch (err) {
    if (isViteDev()) {
      // eslint-disable-next-line no-console
      console.warn("console-config unavailable; using local .env fallback (dev only):", err);
      _config = viteFallbackConfig();
      return _config;
    }
    throw err;
  }
}

export function getConsoleConfig(): ConsoleConfig {
  if (!_config) {
    throw new Error("Console config not loaded. Await loadConsoleConfig() first.");
  }
  return _config;
}

/** Test-only reset so one suite's seed cannot leak into the next. */
export function __resetConsoleConfigForTests(next: Partial<ConsoleConfig> | null): void {
  _config = next ? { ...EMPTY, ...next } : null;
}
